<!--
  OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
  all the essential functionalities required for any enterprise.
  Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com

  OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
  the GNU General Public License as published by the Free Software Foundation, either
  version 3 of the License, or (at your option) any later version.

  OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU General Public License for more details.

  You should have received a copy of the GNU General Public License along with OrangeHRM.
  If not, see <https://www.gnu.org/licenses/>.
-->
<template>
  <div>
    <oxd-dialog class="orangehrm-dialog-popup" @update:show="$emit('close')">
      <!-- Header -->
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">{{ dialogTitle }}</oxd-text>
      </div>
      <oxd-divider />

      <!-- Mode 1: View / Edit single holiday -->
      <div v-if="mode === 'view'">
        <!-- View mode -->
        <div v-if="!isEditing" class="orangehrm-modal-content">
          <oxd-form @submit.prevent>
            <oxd-form-row>
              <oxd-text type="span" class="orangehrm-sub-title">Holiday Name</oxd-text>
              <oxd-text type="p">{{ currentHoliday.name }}</oxd-text>
            </oxd-form-row>
            <oxd-form-row>
              <oxd-text type="span" class="orangehrm-sub-title">Date</oxd-text>
              <oxd-text type="p">{{ currentHoliday.date }}</oxd-text>
            </oxd-form-row>
            <oxd-form-row>
              <oxd-text type="span" class="orangehrm-sub-title">Full Day / Half Day</oxd-text>
              <oxd-text type="p">
                {{ currentHoliday.length === 4 ? 'Half Day' : 'Full Day' }}
              </oxd-text>
            </oxd-form-row>
            <oxd-form-row>
              <oxd-text type="span" class="orangehrm-sub-title">Repeats Annually</oxd-text>
              <oxd-text type="p">
                {{ currentHoliday.recurring ? 'Yes' : 'No' }}
              </oxd-text>
            </oxd-form-row>
          </oxd-form>
        </div>

        <!-- Edit mode -->
        <div v-else class="orangehrm-modal-content">
          <oxd-form @submit.prevent="saveEdit">
            <oxd-form-row>
              <oxd-input-field
                v-model="editForm.name"
                label="Holiday Name"
                :required="true"
                maxlength="200"
              />
            </oxd-form-row>
            <oxd-form-row>
              <date-input v-model="editForm.date" label="Date" :required="true" />
            </oxd-form-row>
            <oxd-form-row>
              <oxd-input-field
                v-model="editForm.length"
                type="select"
                label="Full Day / Half Day"
                :options="LENGTH_OPTIONS"
                :required="true"
              />
            </oxd-form-row>
            <oxd-form-row>
              <oxd-input-group label="Repeats Annually" :classes="{wrapper: '--radio-grouped-field'}">
                <oxd-input-field
                  v-model="editForm.recurring"
                  type="radio"
                  option-label="Yes"
                  :value="true"
                />
                <oxd-input-field
                  v-model="editForm.recurring"
                  type="radio"
                  option-label="No"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-form-row>
          </oxd-form>
        </div>
      </div>

      <!-- Mode 2: Add single holiday -->
      <div v-else-if="mode === 'add'" class="orangehrm-modal-content">
        <oxd-form @submit.prevent="saveAdd">
          <oxd-form-row>
            <oxd-input-field
              v-model="addForm.name"
              label="Holiday Name"
              placeholder="e.g. Diwali"
              :required="true"
              maxlength="200"
            />
          </oxd-form-row>
          <oxd-form-row>
            <date-input v-model="addForm.date" label="Date" :required="true" />
          </oxd-form-row>
          <oxd-form-row>
            <oxd-input-field
              v-model="addForm.length"
              type="select"
              label="Full Day / Half Day"
              :options="LENGTH_OPTIONS"
              :required="true"
            />
          </oxd-form-row>
          <oxd-form-row>
            <oxd-input-group label="Repeats Annually" :classes="{wrapper: '--radio-grouped-field'}">
              <oxd-input-field
                v-model="addForm.recurring"
                type="radio"
                option-label="Yes"
                :value="true"
              />
              <oxd-input-field
                v-model="addForm.recurring"
                type="radio"
                option-label="No"
                :value="false"
              />
            </oxd-input-group>
          </oxd-form-row>
        </oxd-form>
      </div>

      <!-- Mode 3: Import CSV -->
      <div v-else-if="mode === 'import'" class="orangehrm-modal-content">
        <oxd-form @submit.prevent>
          <oxd-form-row>
            <oxd-text type="card-body">
              Paste lines in <code>YYYY-MM-DD, Holiday Name, [Full Day | Half Day], [Repeats: Yes | No]</code> format.
            </oxd-text>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-button
              display-type="ghost"
              :label="`Populate 3rd Saturdays (${effectiveYear})`"
              icon-name="calendar-plus"
              @click="populateThirdSaturdays"
            />
          </oxd-form-row>

          <!-- Default Duration & Repeats Annually for CSV import when row omits them -->
          <oxd-form-row>
            <oxd-input-field
              v-model="importDefaults.length"
              type="select"
              label="Default Duration (if omitted in CSV)"
              :options="LENGTH_OPTIONS"
            />
          </oxd-form-row>

          <oxd-form-row>
            <oxd-input-group label="Default Repeats Annually (if omitted in CSV)" :classes="{wrapper: '--radio-grouped-field'}">
              <oxd-input-field
                v-model="importDefaults.recurring"
                type="radio"
                option-label="Yes"
                :value="true"
              />
              <oxd-input-field
                v-model="importDefaults.recurring"
                type="radio"
                option-label="No"
                :value="false"
              />
            </oxd-input-group>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-input-field
              v-model="csvText"
              type="textarea"
              label="CSV Content"
              placeholder="2026-01-01, New Year's Day, Full Day, Yes&#10;2026-01-26, Republic Day, Full Day, Yes&#10;2026-08-15, Independence Day, Full Day, Yes&#10;2026-10-02, Gandhi Jayanti, Half Day, No"
            />
          </oxd-form-row>

          <oxd-form-row v-if="parsedHolidays.length > 0">
            <oxd-text type="span" class="orangehrm-sub-title">
              Ready to import: {{ parsedHolidays.length }} holiday(s)
            </oxd-text>
          </oxd-form-row>

          <!-- Preview of parsed holidays -->
          <div v-if="parsedHolidays.length > 0" class="cal-more-list" style="max-height: 180px; margin-bottom: 1rem;">
            <div
              v-for="(h, idx) in parsedHolidays.slice(0, 10)"
              :key="idx"
              class="cal-more-item"
              style="cursor: default;"
            >
              <div class="cal-more-item-time">{{ h.date }}</div>
              <div class="cal-more-item-details">
                <span class="cal-more-item-title">{{ h.name }}</span>
                <span class="cal-more-item-loc">
                  {{ h.length === 4 ? 'Half Day' : 'Full Day' }} &middot;
                  {{ h.recurring ? 'Repeats Annually' : 'One-time' }}
                </span>
              </div>
            </div>
            <div v-if="parsedHolidays.length > 10" class="cal-more-item-loc" style="text-align: center; padding: 4px;">
              ... and {{ parsedHolidays.length - 10 }} more
            </div>
          </div>

          <oxd-form-row v-if="importProgress">
            <oxd-text type="card-body">{{ importProgress }}</oxd-text>
          </oxd-form-row>
        </oxd-form>
      </div>

      <oxd-text v-if="errorMsg" class="cal-sheet-error orangehrm-left-space" type="p">
        {{ errorMsg }}
      </oxd-text>

      <oxd-divider />

      <!-- Dialog Footer -->
      <div class="orangehrm-modal-footer">
        <!-- View mode -->
        <template v-if="mode === 'view'">
          <template v-if="!isEditing">
            <oxd-button
              display-type="ghost"
              label="Close"
              @click="$emit('close')"
            />
            <oxd-button
              v-if="isAdmin"
              display-type="label-danger"
              label="Delete"
              icon-name="trash"
              class="orangehrm-left-space"
              :disabled="isDeleting"
              @click="onDeleteHoliday"
            />
            <oxd-button
              v-if="isAdmin"
              display-type="secondary"
              label="Edit"
              icon-name="pencil"
              class="orangehrm-left-space"
              @click="startEdit"
            />
          </template>
          <template v-else>
            <oxd-button
              display-type="ghost"
              label="Cancel"
              @click="cancelEdit"
            />
            <oxd-button
              display-type="secondary"
              label="Save Changes"
              class="orangehrm-left-space"
              :disabled="isSaving"
              @click="saveEdit"
            />
          </template>
        </template>

        <!-- Add mode -->
        <template v-else-if="mode === 'add'">
          <oxd-button
            display-type="ghost"
            label="Cancel"
            @click="$emit('close')"
          />
          <oxd-button
            display-type="secondary"
            label="Save Holiday"
            class="orangehrm-left-space"
            :disabled="isSaving"
            @click="saveAdd"
          />
        </template>

        <!-- Import mode -->
        <template v-else-if="mode === 'import'">
          <oxd-button
            display-type="ghost"
            label="Cancel"
            @click="$emit('close')"
          />
          <oxd-button
            display-type="secondary"
            :label="importButtonLabel"
            class="orangehrm-left-space"
            :disabled="isImporting || parsedHolidays.length === 0"
            @click="importHolidays"
          />
        </template>
      </div>
    </oxd-dialog>

    <!-- OXD Native Delete Confirmation Dialog -->
    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {reactive, ref, computed, watch} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {formatDateKey, generateThirdSaturdaysCSV} from '../utils/calendarUtils';
import {
  OxdDialog,
  OxdButton,
  OxdText,
  OxdDivider,
  OxdForm,
  OxdFormRow,
  OxdInputField,
  OxdInputGroup,
} from '@ohrm/oxd';
import DateInput from '@/core/components/inputs/DateInput.vue';
import DeleteConfirmationDialog from '@/core/components/dialogs/DeleteConfirmationDialog.vue';

const LENGTH_OPTIONS = [
  {id: 0, label: 'Full Day'},
  {id: 4, label: 'Half Day'},
];

export default {
  name: 'HolidayDialog',

  components: {
    DateInput,
    'oxd-dialog': OxdDialog,
    'oxd-button': OxdButton,
    'oxd-text': OxdText,
    'oxd-divider': OxdDivider,
    'oxd-form': OxdForm,
    'oxd-form-row': OxdFormRow,
    'oxd-input-field': OxdInputField,
    'oxd-input-group': OxdInputGroup,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  props: {
    mode: {type: String, default: 'view'}, // 'view' | 'add' | 'import'
    holiday: {type: Object, default: null},
    allHolidays: {type: Array, default: () => []},
    preDate: {type: [Date, String], default: null},
    currentYear: {type: Number, default: () => new Date().getFullYear()},
    isAdmin: {type: Boolean, default: false},
  },

  emits: ['close', 'saved', 'deleted'],

  setup(props, {emit}) {
    const isEditing = ref(false);
    const isSaving = ref(false);
    const isDeleting = ref(false);
    const isImporting = ref(false);
    const importProgress = ref('');
    const errorMsg = ref('');
    const selectedFileName = ref('');
    const fileInputRef = ref(null);
    const deleteDialog = ref(null);
    const csvText = ref('');

    const effectiveYear = computed(() => {
      if (props.currentYear) return props.currentYear;
      if (props.preDate) {
        const d =
          props.preDate instanceof Date
            ? props.preDate
            : new Date(props.preDate);
        if (!isNaN(d.getFullYear())) return d.getFullYear();
      }
      return new Date().getFullYear();
    });

    function populateThirdSaturdays() {
      const generated = generateThirdSaturdaysCSV(effectiveYear.value);
      if (csvText.value.trim()) {
        csvText.value = `${csvText.value.trim()}\n${generated}`;
      } else {
        csvText.value = generated;
      }
    }

    const currentHoliday = reactive({
      id: null,
      name: '',
      date: '',
      length: 0,
      recurring: false,
    });

    const editForm = reactive({
      name: '',
      date: '',
      length: LENGTH_OPTIONS[0],
      recurring: false,
    });

    const addForm = reactive({
      name: '',
      date: '',
      length: LENGTH_OPTIONS[0],
      recurring: false,
    });

    const importDefaults = reactive({
      length: LENGTH_OPTIONS[0],
      recurring: false,
    });

    const holidayHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/holidays',
    );

    // Sync currentHoliday with props.holiday
    watch(
      () => props.holiday,
      (h) => {
        if (h) {
          currentHoliday.id = h.id ?? null;
          currentHoliday.name = h.name || '';
          currentHoliday.date = h.date || '';
          currentHoliday.length = h.length ?? 0;
          currentHoliday.recurring = Boolean(h.recurring);
          if (props.mode === 'add') {
            addForm.name = h.name || '';
            addForm.length =
              LENGTH_OPTIONS.find((opt) => opt.id === h.length) ||
              LENGTH_OPTIONS[0];
            addForm.recurring = Boolean(h.recurring);
          }
        } else {
          currentHoliday.id = null;
          currentHoliday.name = '';
          currentHoliday.date = '';
          currentHoliday.length = 0;
          currentHoliday.recurring = false;
          if (props.mode === 'add') {
            addForm.name = '';
          }
        }
        isEditing.value = false;
        errorMsg.value = '';
      },
      {immediate: true},
    );

    // Sync preDate with addForm.date
    watch(
      () => props.preDate,
      (val) => {
        if (val) {
          addForm.date =
            val instanceof Date ? formatDateKey(val) : String(val);
        } else {
          addForm.date = formatDateKey(new Date());
        }
      },
      {immediate: true},
    );

    const dialogTitle = computed(() => {
      if (props.mode === 'add') return 'Add Holiday';
      if (props.mode === 'import') return 'Import Holidays';
      return isEditing.value ? 'Edit Holiday' : 'Holiday Details';
    });

    function startEdit() {
      editForm.name = currentHoliday.name;
      editForm.date = currentHoliday.date;
      editForm.length =
        LENGTH_OPTIONS.find((opt) => opt.id === currentHoliday.length) ||
        LENGTH_OPTIONS[0];
      editForm.recurring = Boolean(currentHoliday.recurring);
      errorMsg.value = '';
      isEditing.value = true;
    }

    function cancelEdit() {
      isEditing.value = false;
      errorMsg.value = '';
    }

    async function saveEdit() {
      if (!editForm.name.trim()) {
        errorMsg.value = 'Holiday name is required.';
        return;
      }
      if (!editForm.date) {
        errorMsg.value = 'Date is required.';
        return;
      }

      isSaving.value = true;
      errorMsg.value = '';

      const lengthId =
        typeof editForm.length === 'object' && editForm.length !== null
          ? editForm.length.id
          : editForm.length ?? 0;

      try {
        await holidayHttp.update(currentHoliday.id, {
          name: editForm.name.trim(),
          date: editForm.date,
          length: Number(lengthId),
          recurring: Boolean(editForm.recurring),
        });

        currentHoliday.name = editForm.name.trim();
        currentHoliday.date = editForm.date;
        currentHoliday.length = Number(lengthId);
        currentHoliday.recurring = Boolean(editForm.recurring);
        isEditing.value = false;
        emit('saved');
      } catch (err) {
        const msg =
          err?.response?.data?.error?.message ||
          'Failed to update holiday. Please try again.';
        errorMsg.value = msg;
      } finally {
        isSaving.value = false;
      }
    }

    async function saveAdd() {
      if (!addForm.name.trim()) {
        errorMsg.value = 'Holiday name is required.';
        return;
      }
      if (!addForm.date) {
        errorMsg.value = 'Date is required.';
        return;
      }

      isSaving.value = true;
      errorMsg.value = '';

      const lengthId =
        typeof addForm.length === 'object' && addForm.length !== null
          ? addForm.length.id
          : addForm.length ?? 0;

      try {
        await holidayHttp.create({
          name: addForm.name.trim(),
          date: addForm.date,
          length: Number(lengthId),
          recurring: Boolean(addForm.recurring),
        });

        emit('saved');
        emit('close');
      } catch (err) {
        const msg =
          err?.response?.data?.error?.message ||
          'Failed to create holiday. Please try again.';
        errorMsg.value = msg;
      } finally {
        isSaving.value = false;
      }
    }

    async function onDeleteHoliday() {
      if (!deleteDialog.value) return;

      const confirmed = await deleteDialog.value.showDialog();
      if (confirmed !== 'ok') return;

      isDeleting.value = true;
      errorMsg.value = '';

      try {
        const ids =
          props.allHolidays && props.allHolidays.length > 0
            ? props.allHolidays.map((h) => h.id).filter(Boolean)
            : [currentHoliday.id];
        await holidayHttp.deleteAll({ids});
        emit('deleted');
        emit('close');
      } catch (err) {
        const msg =
          err?.response?.data?.error?.message || 'Failed to delete holiday.';
        errorMsg.value = msg;
      } finally {
        isDeleting.value = false;
      }
    }

    // CSV file parsing
    function triggerFileInput() {
      if (fileInputRef.value) {
        fileInputRef.value.value = '';
        fileInputRef.value.click();
      }
    }

    function onFileChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      selectedFileName.value = file.name;
      const reader = new FileReader();
      reader.onload = (event) => {
        csvText.value = event.target.result;
      };
      reader.readAsText(file);
    }

    function parseDuration(val, defaultVal = 0) {
      if (!val) return defaultVal;
      const clean = String(val).trim().toLowerCase();
      if (clean.includes('half') || clean === '4') return 4;
      if (clean.includes('full') || clean === '0' || clean === '8') return 0;
      return defaultVal;
    }

    function parseRecurring(val, defaultVal = false) {
      if (!val) return defaultVal;
      const clean = String(val).trim().toLowerCase();
      if (
        ['yes', 'true', '1', 'y', 'annual', 'annually', 'repeat'].includes(
          clean,
        )
      ) {
        return true;
      }
      if (['no', 'false', '0', 'n'].includes(clean)) {
        return false;
      }
      return defaultVal;
    }

    const parsedHolidays = computed(() => {
      if (!csvText.value.trim()) return [];

      const lines = csvText.value.split(/\r?\n/);
      const results = [];

      const defLengthId =
        typeof importDefaults.length === 'object' &&
        importDefaults.length !== null
          ? importDefaults.length.id
          : importDefaults.length ?? 0;
      const defRecurring = Boolean(importDefaults.recurring);

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || line.startsWith('#')) continue;

        const parts = line.split(',');
        if (parts.length >= 2) {
          const date = parts[0].trim();
          const name = parts[1].trim();

          if (/^\d{4}-\d{2}-\d{2}$/.test(date) && name) {
            let length = defLengthId;
            let recurring = defRecurring;

            if (parts.length === 3) {
              const third = parts[2].trim().toLowerCase();
              if (
                [
                  'yes',
                  'true',
                  '1',
                  'y',
                  'no',
                  'false',
                  '0',
                  'n',
                  'annual',
                  'annually',
                ].includes(third)
              ) {
                recurring = parseRecurring(third, defRecurring);
              } else {
                length = parseDuration(third, defLengthId);
              }
            } else if (parts.length >= 4) {
              length = parseDuration(parts[2], defLengthId);
              recurring = parseRecurring(parts[3], defRecurring);
            }

            results.push({date, name, length, recurring});
          }
        }
      }

      return results;
    });

    const importButtonLabel = computed(() => {
      if (isImporting.value) return 'Importing…';
      if (parsedHolidays.value.length > 0) {
        return `Import (${parsedHolidays.value.length})`;
      }
      return 'Import';
    });

    async function importHolidays() {
      const items = parsedHolidays.value;
      if (items.length === 0) return;

      isImporting.value = true;
      errorMsg.value = '';

      let successCount = 0;
      let failureCount = 0;

      const CONCURRENCY = 5;
      for (let i = 0; i < items.length; i += CONCURRENCY) {
        const chunk = items.slice(i, i + CONCURRENCY);
        importProgress.value = `Importing ${Math.min(
          i + 1,
          items.length,
        )}–${Math.min(i + chunk.length, items.length)} of ${items.length}…`;

        const promises = chunk.map((h) =>
          holidayHttp
            .create({
              name: h.name,
              date: h.date,
              length: h.length,
              recurring: h.recurring,
            })
            .then(() => {
              successCount++;
            })
            .catch(() => {
              failureCount++;
            }),
        );

        await Promise.all(promises);
      }

      isImporting.value = false;
      importProgress.value = '';

      if (failureCount > 0) {
        errorMsg.value = `Import finished: ${successCount} added, ${failureCount} failed (possible duplicates).`;
      }

      if (successCount > 0) {
        emit('saved');
        if (failureCount === 0) {
          emit('close');
        }
      }
    }

    return {
      isEditing,
      isSaving,
      isDeleting,
      isImporting,
      importProgress,
      errorMsg,
      selectedFileName,
      fileInputRef,
      deleteDialog,
      csvText,
      currentHoliday,
      editForm,
      addForm,
      importDefaults,
      LENGTH_OPTIONS,
      dialogTitle,
      startEdit,
      cancelEdit,
      saveEdit,
      saveAdd,
      onDeleteHoliday,
      triggerFileInput,
      onFileChange,
      parsedHolidays,
      importButtonLabel,
      importHolidays,
      effectiveYear,
      populateThirdSaturdays,
    };
  },
};
</script>
<style lang="scss" scoped>
.orangehrm-modal-header {
  margin-bottom: 1.2rem;
  display: flex;
  justify-content: center;
}

.orangehrm-modal-content {
  margin-top: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}

.orangehrm-modal-footer {
  margin-top: 1.2rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}
</style>
